import { ConfirmActionPopup } from "@/components/popups/confirm-action";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePermission } from "@/hooks/use-premission";
import { useDeleteTemplate } from "@/hooks/use-template";
import { cn } from "@/lib/utils";
import { useTemplateStore } from "@/stores/template.store";
import { TemplateType } from "@/types/template.types";
import { Link } from "@tanstack/react-router";
import { EllipsisVertical, Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import Trash from "@/components/icons/trash";

type Props = {
  data: TemplateType;
  triggerClassName?: string;
  type: "grid" | "list";
};

const TemplateActions = ({ data, triggerClassName, type }: Props) => {
  const [deletePopup, setDeletePopup] = useState<boolean>(false);
  const { mutateAsync: deleteTemplate } = useDeleteTemplate();
  const { storeTemplate, setReport, clearReport } = useTemplateStore();
  const { hasPermission } = usePermission();

  const handleDelete = async (id: string) => {
    try {
      await deleteTemplate(id);
      toast.success("Template deleted successfully");
      window.cioanalytics?.track("template_deleted", {
        templateName: data.title,
        templateType: data.contentType,
        from: type,
      });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const hasEditPermission = hasPermission("templates:edit");
  const hasDeletePermission = hasPermission("templates:delete");

  if (!hasEditPermission && !hasDeletePermission) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              type === "grid"
                ? "h-auto p-1 invisible group-hover:visible data-[state=open]:visible rounded-lg"
                : "",
              triggerClassName
            )}
          >
            <EllipsisVertical size={10} className="text-gray-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {hasEditPermission && (
            <DropdownMenuItem asChild>
              <Link
                to="/templates/editor"
                search={{ id: data._id }}
                onClick={() => {
                  storeTemplate(data);
                  if (data?.reportId) {
                    setReport(data.reportId.report);
                  } else {
                    clearReport();
                  }
                }}
              >
                <Pencil size={16} />
                Edit
              </Link>
            </DropdownMenuItem>
          )}

          {hasDeletePermission && (
            <DropdownMenuItem
              className="delete-button group"
              onClick={() => setDeletePopup(true)}
            >
              <Trash className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete template popup */}
      {deletePopup && (
        <ConfirmActionPopup
          open={deletePopup}
          onOpenChange={(open) => !open && setDeletePopup(false)}
          title="Delete Template"
          description={
            <span>
              Are you sure you want to remove{" "}
              <span className="font-bold">{data.title}</span>?
              <span className="block">
                This action will permanently remove the template from all
                associated <span className="font-bold">mailboxes</span> and
                cannot be reversed.
              </span>
            </span>
          }
          actionLabel="Yes, delete"
          iconColor="text-red-600"
          onConfirm={() => handleDelete(data._id)}
        />
      )}
    </>
  );
};

export default TemplateActions;
